import { useRef, useState } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { toast } from "sonner";
import { DownloadSimple, FilePdf, Image as ImageIcon, Check, LockSimple } from "@phosphor-icons/react";
import { POSTCARD_IMG } from "@/lib/brand";

export function PostcardPreview({
  zone,
  title = "9×12 Mailer Preview · Live",
  downloadable = false,
  interactive = false,
  selectedIds = [],
  onSelect,
  generic = false,
}) {
  const cats = zone?.categories || [];
  const backend = process.env.REACT_APP_BACKEND_URL;
  const abs = (u) => (u ? `${u.startsWith("http") ? "" : backend}${u}` : null);
  const frontRef = useRef(null);
  const backRef = useRef(null);
  const [busy, setBusy] = useState("");

  const faces = { Front: [], Back: [] };
  cats.filter((c) => c.hostReserved).forEach((c) => faces[c.face === "Back" ? "Back" : "Front"].push(c));
  cats.filter((c) => !c.hostReserved).sort((a, b) => (a.order ?? 999) - (b.order ?? 999)).forEach((c) => {
    faces[faces.Front.length <= faces.Back.length ? "Front" : "Back"].push(c);
  });

  const isSelected = (c) => selectedIds.includes(c.categoryId);
  const isSelectable = (c) => interactive && !c.hostReserved && !c.unavailable && c.fullAvailable;

  const saleable = cats.filter((c) => !c.hostReserved);
  const openCount = saleable.filter((c) => c.fullAvailable && !c.unavailable).length;
  const totalSale = saleable.length;
  const pct = totalSale ? openCount / totalSale : 0;
  const ringC = 2 * Math.PI * 15;
  const scarce = pct <= 0.3;
  const ringColor = scarce ? "#d97706" : pct <= 0.6 ? "#0f172a" : "#047857";

  const hostName = (c) => c.hostBrand || zone?.hostBrand || "House Reserved";
  const displayName = (c) => {
    if (c.hostReserved) return hostName(c);
    if (c.unavailable) return c.claimedCategory || c.name;
    return generic ? "Available" : c.name;
  };
  const art = (c) => (c.hostReserved ? abs(c.hostLogoUrl) : abs(c.artworkUrl));

  const zoneClass = (c) => {
    if (c.hostReserved) return "bg-indigo-600/90 text-white border-indigo-300";
    if (c.unavailable) return "bg-navy-900/90 text-white border-navy-700";
    if (isSelected(c)) return "bg-emerald-600/95 text-white border-emerald-300 ring-2 ring-emerald-300";
    if (!c.fullAvailable) return "bg-amber-200/90 text-amber-900 border-amber-300";
    return interactive
      ? "bg-white/85 text-navy-900 border-white hover:bg-emerald-50/95 hover:border-emerald-500 cursor-pointer"
      : "bg-white/85 text-navy-900 border-white";
  };
  const label = (c) => {
    if (c.hostReserved) return "Reserved";
    if (c.unavailable) return "Sold";
    if (isSelected(c)) return "Selected";
    if (!c.fullAvailable) return "Unavailable";
    return `$${c.fullPrice} / Slot`;
  };

  const snap = async (node) => html2canvas(node, {
    scale: 2, backgroundColor: "#ffffff", useCORS: true, logging: false,
    ignoreElements: (el) => el.classList && el.classList.contains("pc-bg"),
  });

  const downloadPng = async () => {
    if (!frontRef.current || !backRef.current) return;
    setBusy("png");
    try {
      const [f, b] = [await snap(frontRef.current), await snap(backRef.current)];
      const gap = 24;
      const out = document.createElement("canvas");
      out.width = Math.max(f.width, b.width); out.height = f.height + b.height + gap;
      const ctx = out.getContext("2d");
      ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, out.width, out.height);
      ctx.drawImage(f, 0, 0); ctx.drawImage(b, 0, f.height + gap);
      const link = document.createElement("a");
      link.download = `mailer-${zone?.zipCode || "preview"}.png`;
      link.href = out.toDataURL("image/png"); link.click();
      toast.success("Front + Back PNG downloaded");
    } catch { toast.error("Could not export PNG"); }
    finally { setBusy(""); }
  };

  const downloadPdf = async () => {
    if (!frontRef.current || !backRef.current) return;
    setBusy("pdf");
    try {
      const canvases = [["Front", await snap(frontRef.current)], ["Back", await snap(backRef.current)]];
      const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "letter" });
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      canvases.forEach(([name, canvas], i) => {
        const margin = 40;
        const ratio = Math.min((pw - margin * 2) / canvas.width, (ph - margin * 2 - 30) / canvas.height);
        const iw = canvas.width * ratio, ih = canvas.height * ratio;
        if (i > 0) pdf.addPage();
        pdf.setFontSize(14); pdf.setTextColor("#0f172a");
        pdf.text(`${zone?.zipCode ? `Zip ${zone.zipCode} — ` : ""}${name} · 9×12 Mailer`, margin, margin);
        pdf.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", (pw - iw) / 2, margin + 14, iw, ih);
      });
      pdf.save(`mailer-${zone?.zipCode || "preview"}.pdf`);
      toast.success("2-page PDF downloaded");
    } catch (e) { console.error("PDF export error:", e); toast.error("Could not export PDF"); }
    finally { setBusy(""); }
  };

  const Face = ({ name, items, innerRef }) => (
    <div className="flex-1 min-w-[280px]" data-testid={`postcard-face-${name}`}>
      <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400 mb-1.5">{name} · 9×12 Oversized</p>
      <div ref={innerRef} className="relative w-full border border-slate-300 bg-slate-100 overflow-hidden">
        <img src={POSTCARD_IMG} alt={`${name} postcard`} className="pc-bg w-full h-auto block select-none pointer-events-none" draggable={false} />
        <div className="absolute inset-0 p-[3%] grid grid-cols-4 grid-rows-2 gap-[1.2%]">
          {items.slice(0, 8).map((c) => {
            const img = art(c);
            const sel = isSelectable(c);
            return (
              <div
                key={c.categoryId}
                data-testid={`preview-block-${name}-${c.categoryId}`}
                role={sel ? "button" : undefined}
                onClick={sel ? () => onSelect && onSelect(c) : undefined}
                className={`relative border rounded-[2px] shadow-sm text-[9px] font-bold uppercase tracking-wide flex flex-col items-center justify-center text-center px-1 py-0.5 overflow-hidden transition-colors ${zoneClass(c)}`}
              >
                {isSelected(c) && <Check size={11} weight="bold" className="absolute top-0.5 right-0.5" />}
                {c.hostReserved && <LockSimple size={9} className="absolute top-0.5 right-0.5 opacity-80" />}
                {img ? <img src={img} alt={c.name} crossOrigin="anonymous" className="max-h-[38%] max-w-[80%] object-contain mb-0.5" /> : null}
                <span className="leading-tight line-clamp-2">{displayName(c)}</span>
                <span className="opacity-80 text-[8px] mt-0.5">{label(c)}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  if (!cats.length) return null;

  return (
    <div className="mb-6 bg-white border border-slate-200 p-4" data-testid="postcard-preview">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-3">
          {totalSale > 0 && (
            <div className="relative shrink-0" data-testid="scarcity-ring" title={`${openCount} of ${totalSale} categories open`}>
              <svg width="40" height="40" viewBox="0 0 40 40" className="-rotate-90">
                <circle cx="20" cy="20" r="15" fill="none" stroke="#e2e8f0" strokeWidth="4" />
                <circle cx="20" cy="20" r="15" fill="none" stroke={ringColor} strokeWidth="4" strokeLinecap="round"
                  strokeDasharray={ringC} strokeDashoffset={ringC * (1 - pct)} style={{ transition: "stroke-dashoffset .5s ease" }} />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold" style={{ color: ringColor }}>{openCount}</span>
            </div>
          )}
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">{title}</p>
            {totalSale > 0 && (
              <p className={`text-[11px] font-[600] ${scarce ? "text-amber-600" : "text-slate-500"}`} data-testid="scarcity-label">
                {openCount} of {totalSale} slots open{scarce && openCount > 0 ? " · going fast" : openCount === 0 ? " · fully booked" : ""}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-3 text-[9px] font-bold uppercase tracking-wider text-slate-500">
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-indigo-600 inline-block" /> Host</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-navy-900 inline-block" /> Sold</span>
          {interactive && <span className="flex items-center gap-1"><span className="w-3 h-3 bg-emerald-600 inline-block" /> Selected</span>}
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-white border border-slate-300 inline-block" /> Open</span>
        </div>
      </div>
      {interactive && <p className="text-[11px] text-slate-500 mb-3" data-testid="preview-hint">Tap an open slot directly on the postcard to add it to your order — each Full Slot is ${cats.find((c) => !c.hostReserved)?.fullPrice ?? 500}.</p>}
      {downloadable && (
        <div className="flex items-center gap-2 mb-3" data-testid="preview-download-bar">
          <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400 flex items-center gap-1"><DownloadSimple size={13} /> Pitch export</span>
          <button onClick={downloadPng} disabled={!!busy} data-testid="download-png-btn"
            className="flex items-center gap-1.5 border border-slate-300 px-3 py-1.5 text-xs font-[600] hover:border-navy-900 hover:bg-slate-50 transition-colors disabled:opacity-50">
            <ImageIcon size={14} /> {busy === "png" ? "Exporting…" : "PNG"}
          </button>
          <button onClick={downloadPdf} disabled={!!busy} data-testid="download-pdf-btn"
            className="flex items-center gap-1.5 border border-slate-300 px-3 py-1.5 text-xs font-[600] hover:border-navy-900 hover:bg-slate-50 transition-colors disabled:opacity-50">
            <FilePdf size={14} /> {busy === "pdf" ? "Exporting…" : "PDF"}
          </button>
        </div>
      )}
      <div className="flex flex-col md:flex-row gap-4">
        <Face name="Front" items={faces.Front} innerRef={frontRef} />
        <Face name="Back" items={faces.Back} innerRef={backRef} />
      </div>
    </div>
  );
}
